<template>
  <aside class="gm-sidebar">
    <button v-if="canSend" class="gm-compose-btn" @click="$emit('compose')" :disabled="!selectedAccount">
      <b-icon icon="pencil-square" class="mr-2" /> {{ $t('inbox.compose') }}
    </button>

    <nav class="gm-nav">
      <button
        v-for="f in folders" :key="f.key"
        class="gm-nav-item"
        :class="{ 'gm-nav-item--active': currentFolder === f.key, 'gm-nav-item--starred': f.key === 'Starred' }"
        @click="$emit('folder-change', f.key)"
      >
        <b-icon :icon="f.icon" class="gm-nav-item__icon" />
        <span class="gm-nav-item__label">{{ f.label }}</span>
        <span v-if="f.key === 'INBOX' && unreadCount > 0" class="gm-nav-item__badge">{{ unreadCount }}</span>
        <span v-if="f.key === 'Drafts' && hasDraft && !showCompose" class="gm-nav-item__badge gm-nav-item__badge--draft">1</span>
      </button>
    </nav>

    <div class="gm-sidebar-divider" />
    <div class="gm-sidebar-section-label" style="display:flex;align-items:center;justify-content:space-between;padding-right:0.5rem">
      <span>{{ $t('inbox.folders') }}</span>
      <button class="gm-sidebar-icon-btn" @click="$emit('create-folder')" v-b-tooltip.hover :title="$t('inbox.newFolder')">
        <b-icon icon="folder-plus" />
      </button>
    </div>
    <div
      v-for="f in imapFolders" :key="f.name"
      class="gm-nav-item"
      :class="{ 'gm-nav-item--active': currentFolder === f.name }"
      @click="$emit('folder-change', f.name)"
    >
      <b-icon icon="folder" class="gm-nav-item__icon" />
      <span class="gm-nav-item__label">{{ f.name }}</span>
    </div>

    <div class="gm-sidebar-divider" />
    <div class="gm-sidebar-section-label">{{ $t('inbox.myAccounts') }}</div>
    <div
      v-for="acc in myAccounts" :key="acc.id"
      class="gm-account-item"
      :class="{ 'gm-account-item--active': selectedAccount && selectedAccount.id === acc.id }"
      @click="$emit('account-select', acc)"
    >
      <div class="gm-account-avatar">{{ (acc.email || acc.name || '?')[0].toUpperCase() }}</div>
      <div class="gm-account-info">
        <div class="gm-account-email" :title="acc.email">{{ acc.email }}</div>
        <div class="gm-account-name">{{ acc.name }}</div>
      </div>
      <button class="gm-account-action-btn" @click.stop="$emit('account-options', acc)" v-b-tooltip.hover :title="$t('inbox.accountOptions')">
        <b-icon icon="three-dots-vertical" />
      </button>
    </div>
    <button class="gm-add-account-btn" @click="$emit('add-account')">
      <b-icon icon="plus-circle" class="mr-1" /> {{ $t('inbox.addAccount') }}
    </button>

    <template v-if="sharedAccounts.length">
      <div class="gm-sidebar-divider" />
      <div class="gm-sidebar-section-label">{{ $t('inbox.sharedAccounts') }}</div>
      <div
        v-for="acc in sharedAccounts" :key="'shared-' + acc.id"
        class="gm-account-item"
        :class="{ 'gm-account-item--active': selectedAccount && selectedAccount.id === acc.id }"
        @click="$emit('account-select', acc)"
      >
        <div class="gm-account-avatar gm-account-avatar--shared">{{ (acc.email || acc.name || '?')[0].toUpperCase() }}</div>
        <div class="gm-account-info">
          <div class="gm-account-email" :title="acc.email">{{ acc.email }}</div>
          <div class="gm-account-name">{{ acc.name }}</div>
        </div>
      </div>
    </template>

    <div class="gm-sidebar-spacer" />
    <div class="gm-sidebar-divider" />
    <div v-if="selectedAccount" class="gm-profile-bar" @click="$emit('manage-accounts')">
      <div class="gm-account-avatar gm-account-avatar--sm">{{ (selectedAccount.email || selectedAccount.name || '?')[0].toUpperCase() }}</div>
      <div class="gm-profile-bar__body">
        <div class="gm-profile-bar__email">{{ selectedAccount.email }}</div>
        <div class="gm-profile-bar__hint">{{ $t('inbox.manageAccounts') }}</div>
      </div>
      <b-icon icon="gear" class="gm-profile-bar__gear" />
    </div>
  </aside>
</template>

<script>
export default {
  name: 'InboxSidebar',
  props: {
    folders: { type: Array, required: true },
    currentFolder: { type: String, required: true },
    unreadCount: { type: Number, default: 0 },
    hasDraft: { type: Boolean, default: false },
    showCompose: { type: Boolean, default: false },
    imapFolders: { type: Array, default: () => [] },
    accounts: { type: Array, default: () => [] },
    selectedAccount: { type: Object, default: null },
    canSend: { type: Boolean, default: true }
  },
  computed: {
    myAccounts() {
      return this.accounts.filter(a => a.is_owner);
    },
    sharedAccounts() {
      return this.accounts.filter(a => !a.is_owner);
    }
  }
};
</script>

<style scoped>
.gm-sidebar {
  width: 196px;
  flex-shrink: 0;
  background: var(--bg-secondary, #1e1f26);
  border-right: 1px solid var(--border-separator, #2a2d35);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0.75rem 0.5rem;
}

.gm-compose-btn {
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  transition: transform 0.12s, box-shadow 0.12s;
}
.gm-compose-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}
.gm-compose-btn:active:not(:disabled) {
  transform: translateY(0);
}
.gm-compose-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.gm-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 0.5rem;
}

.gm-nav-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: var(--text-secondary, #9ca3af);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  position: relative;
  text-align: left;
  width: 100%;
}
.gm-nav-item:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}
.gm-nav-item--active {
  background: var(--active-bg, rgba(102,126,234,0.15));
  color: var(--primary-start, #6c80ff);
  font-weight: 600;
}
.gm-nav-item--starred:hover {
  color: #fbbf24;
}
.gm-nav-item--starred.gm-nav-item--active {
  color: #fbbf24;
}

.gm-nav-item__icon {
  font-size: 1rem;
  margin-right: 0.625rem;
  flex-shrink: 0;
}

.gm-nav-item__label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-nav-item__badge {
  background: var(--primary-start, #6c80ff);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  margin-left: auto;
  flex-shrink: 0;
}
.gm-nav-item__badge--draft {
  background: #f59e0b;
}

.gm-sidebar-divider {
  height: 1px;
  background: var(--border-separator, #2a2d35);
  margin: 0.5rem 0;
}

.gm-sidebar-section-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted, #6b7280);
  padding: 0.5rem 0.625rem 0.375rem;
}

.gm-sidebar-icon-btn {
  background: none;
  border: none;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}
.gm-sidebar-icon-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}

.gm-account-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background 0.12s;
  margin-bottom: 2px;
}
.gm-account-item:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
}
.gm-account-item--active {
  background: var(--active-bg, rgba(102,126,234,0.15));
}

.gm-account-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  margin-right: 0.625rem;
}
.gm-account-avatar--shared {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
}

.gm-account-info {
  flex: 1;
  min-width: 0;
}

.gm-account-email {
  font-size: 0.75rem;
  color: var(--text-primary, #e5e7eb);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-account-name {
  font-size: 0.6875rem;
  color: var(--text-muted, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-account-action-btn {
  background: none;
  border: none;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s, color 0.12s;
  flex-shrink: 0;
}
.gm-account-item:hover .gm-account-action-btn {
  opacity: 1;
}
.gm-account-action-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.08));
  color: var(--text-primary, #e5e7eb);
}

.gm-add-account-btn {
  background: transparent;
  border: 1px dashed var(--border-separator, #2a2d35);
  color: var(--text-secondary, #9ca3af);
  border-radius: 6px;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  margin-top: 0.375rem;
}
.gm-add-account-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  border-color: var(--primary-start, #6c80ff);
  color: var(--primary-start, #6c80ff);
}

.gm-sidebar-spacer {
  flex: 1;
  min-height: 1rem;
}

.gm-profile-bar {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  background: var(--hover-bg, rgba(255,255,255,0.04));
  cursor: pointer;
  transition: background 0.12s;
}
.gm-profile-bar:hover {
  background: var(--hover-bg, rgba(255,255,255,0.08));
}

.gm-profile-bar__body {
  flex: 1;
  min-width: 0;
  margin-left: 0.5rem;
}

.gm-profile-bar__email {
  font-size: 0.75rem;
  color: var(--text-primary, #e5e7eb);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-profile-bar__hint {
  font-size: 0.6875rem;
  color: var(--text-muted, #6b7280);
}

.gm-profile-bar__gear {
  font-size: 0.8rem;
  color: var(--text-muted, #6b7280);
  flex-shrink: 0;
}

.gm-account-avatar--sm {
  width: 22px;
  height: 22px;
  font-size: 0.62rem;
  flex-shrink: 0;
}
</style>
